import { useCallback, useEffect, useRef } from "react";

import { useStipulationCategoryStore } from "../features/stipulations/stipulationCategoryStore";

// A scroll must move at least this many pixels (cumulative, ignoring
// direction) before it counts as "the analyst scrolled" — filters out
// trackpad momentum jitter and any one-off layout-induced scroll on mount.
const SCROLL_ARM_THRESHOLD_PX = 15;

// The scroll container's scrollTop must be at or below this many pixels to
// count as "back at the top". Kept at exactly 0 (no tolerance): Income (the
// first scroll-spy section) can become active very close to the top of the
// page, so any tolerance here would keep forcing "All Stips" past the point
// where Income should legitimately start filtering, effectively skipping it
// as the analyst scrolls down.
const SCROLL_TOP_RESET_THRESHOLD_PX = 0;

/**
 * Arms the Stipulations panel's scroll-spy category filter (see
 * `stipulationCategoryStore`) the first time the analyst either scrolls a
 * meaningful amount or types into the Signers form. Until then, the panel
 * shows "All Stips" regardless of what the scroll-spy's IntersectionObserver
 * reports (it always fires once immediately on mount, independent of any
 * real user action).
 *
 * Also reports scroll position back to the store via `setScrollAtTop` on
 * every relevant scroll — this listener is kept attached for the Signers
 * form's entire lifetime (unlike arming, it doesn't stop mattering once
 * armed) so that scrolling back near the very top always snaps the filter
 * back to "All Stips", even though the scroll-spy's IntersectionObserver
 * can't reliably detect that on its own (Income, the first section, can
 * still overlap the activation band at scrollTop 0).
 *
 * Returns a ref-callback to attach to the Signers `<form>` element (in place
 * of a plain `useRef`) — a callback ref fires exactly when the DOM node
 * mounts/unmounts, so listeners are always attached to the real element even
 * if it isn't present on this hook's very first render (e.g. an early-return
 * guard renders `null` before the form's data has loaded).
 * @returns {(element: HTMLFormElement | null) => void} A ref-callback to attach via `ref={registerForm}` on the Signers `<form>`.
 */
export function useArmStipulationCategoryFilter(): (element: HTMLFormElement | null) => void {
  const arm = useStipulationCategoryStore((state) => state.arm);
  const setScrollAtTop = useStipulationCategoryStore((state) => state.setScrollAtTop);
  // eslint-disable-next-line unicorn/no-useless-undefined -- React 19's useRef requires an explicit initial value
  const cleanupReference = useRef<(() => void) | undefined>(undefined);

  // Detach any still-active listeners if the component unmounts entirely.
  useEffect(() => () => cleanupReference.current?.(), []);

  return useCallback(
    (element: HTMLFormElement | null) => {
      cleanupReference.current?.();
      cleanupReference.current = undefined;

      if (!element) {
        return;
      }

      const lastScrollPositions = new WeakMap<EventTarget, number>();

      const handleScroll = (event: Event) => {
        const target = event.target;
        const scrolledNode = target instanceof Node ? target : document;
        // Ignore scrolls on unrelated scroll containers (e.g. the
        // Stipulations panel's own list, or the Documents/Messages panel) —
        // only a scroll that actually repositions the Signers form is
        // relevant here.
        if (!scrolledNode.contains(element)) {
          return;
        }
        const scrollTop = target instanceof Element ? target.scrollTop : window.scrollY;

        setScrollAtTop(scrollTop <= SCROLL_TOP_RESET_THRESHOLD_PX);

        const last = lastScrollPositions.get(target ?? window);
        if (last === undefined) {
          lastScrollPositions.set(target ?? window, scrollTop);
          return;
        }
        if (Math.abs(scrollTop - last) >= SCROLL_ARM_THRESHOLD_PX) {
          arm();
        }
      };

      const handleInput = () => arm();

      // A capturing `scroll` listener is required here — this measures actual
      // user scroll distance to gate the scroll-spy, which IntersectionObserver
      // cannot report (it only tells us what's visible, not whether/how far
      // the page moved to get there).
      // eslint-disable-next-line github/prefer-observers -- see comment above; IntersectionObserver can't measure scroll distance
      window.addEventListener("scroll", handleScroll, { capture: true });
      element.addEventListener("input", handleInput);

      cleanupReference.current = () => {
        window.removeEventListener("scroll", handleScroll, { capture: true });
        element.removeEventListener("input", handleInput);
      };
    },
    [arm, setScrollAtTop]
  );
}
